import { useState, useEffect } from 'react';
import useUser from 'helpers/hooks/useUser';
import { Button, Input, Select, Icon, Header, BadgedIcon, Modal } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { RoleComponent } from 'components/roleComponent';
import useFetch from 'helpers/hooks/useFetch';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { HSL, COMPANY_MANAGER, notificationAtom } from 'resources';
import { ActionDTO, FilterInventoryDTO, InventoryData, SelectOption } from 'models/hsl/general';
import { downloadFileExcelInventory } from 'resources/api/hsl_api/fileReports';
import { hslCountriesAtom, hslPartnersAtom, hslPartsAtom } from 'resources/atoms/hslAtom';
import { CompanyDTO } from 'models/userManager/addCustomer';

const InitialFilter: FilterInventoryDTO = {
    CompanyName: [],
    Country: [],
    PartNumber: [],
    PartNumberDescription: '',
    ProductName: [],
    PageIndex: 1,
    PageSize: 10
};

function InventorySearch() {
    const navigate = useNavigate();
    const [partners, setPartners] = useRecoilState(hslPartnersAtom);
    const [countries, setCountries] = useRecoilState(hslCountriesAtom);
    const [parts, setParts] = useRecoilState(hslPartsAtom);
    const { t } = useTranslation(['common', 'hsl', 'user', 'home']);
    const { user: userDetails } = useUser();
    const setNotification = useSetRecoilState(notificationAtom);
    const [, setLoading] = useState<boolean>(false);
    const [openModal, setOpenModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<InventoryData | null>(null);
    const [newAction, setNewAction] = useState<ActionDTO>({
        Action: '',
        Quantity: 0,
        Description: '',
        TrackingNumber: '',
        Observation: ''
    });
    const [filters, setFilters] = useState(InitialFilter);
    const [filteredData, setFilteredData] = useState<InventoryData[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [totalItemsDelivery, setTotalItemsDelivery] = useState<number>(0);
    const [productOptions, setProductOptions] = useState<SelectOption[]>([]);
    const [partsOptions, setPartsOptions] = useState<SelectOption[]>([]);

    const handleSuccess = () => {
        setNotification({ type: 'success', message: 'Report' });
    };
    const handleError = () => {
        setNotification({ type: 'information', message: 'NoData' });
    };

    const [, getDeliveries] = useFetch<null, string[]>({
        url: HSL.getPendentDeliveries,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, getProducts] = useFetch<any, any>({
        url: HSL.getProducts,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [items, getInventoryItems, loadingItems] = useFetch<FilterInventoryDTO, any>({
        url: HSL.getInventoryItems,
        method: 'POST',
        onError: t('common:errors.Unexpected')
    });

    const [, createNewAction, loadingAction] = useFetch<ActionDTO, any>({
        url: HSL.createNewAction,
        method: 'POST',
        onError: t('common:errors.Unexpected')
    });

    const [, getParts] = useFetch({
        url: HSL.getParts,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, getPartners] = useFetch({
        url: COMPANY_MANAGER.getHslPartners,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    useEffect(() => {
        getFilteredItems(filters);
        getPendentDeliveries();
        fetchData(getProducts, setProductOptions);
        if (!partners.length) {
            getAllPartners();
        }
        if (!parts.length) {
            getAllParts();
        }
    }, []);

    useEffect(() => {
        setFilteredData(items?.data.Data);
    }, [items]);

    useEffect(() => {
        const partsOptions = parts.map(part => ({
            value: part.PartNumber,
            text: part.PartNumber
        }));
        setPartsOptions(partsOptions);
    }, [parts]);

    const fetchData = async (fetchFunction: any, setOptions: any) => {
        await fetchFunction(null, (current: any) => {
            if (current?.data) {
                const options = current?.data?.map((item: any) => ({
                    value: item.PartNumber || item.Name || item,
                    text: item.PartNumber || item.Name || item
                }));
                setOptions(options);
            }
        });
    };

    const getFilteredItems = async (filter: FilterInventoryDTO) => {
        await getInventoryItems(filter, resp => {
            if (resp.data.Data) {
                setFilteredData(resp.data.Data);
                setTotalItems(resp.data.TotalCount);
            }
        });
    };

    const getPendentDeliveries = () => {
        getDeliveries(null, resp => {
            if (resp.data) {
                setTotalItemsDelivery(resp.data.length);
            }
        });
    };

    const handleSave = async () => {
        await createNewAction(
            {
                ItemInventoryId: selectedItem?.Id,
                Action: newAction.Action,
                Quantity: newAction.Quantity,
                Observation: newAction.Observation,
                TrackingNumber: newAction.TrackingNumber,
                Description: 'Update By Admin User'
            },
            () => {
                setOpenModal(false);
                navigate(`inventory-details/${selectedItem?.Id}`);
            }
        );
    };

    const handleExportExcel = async () => {
        setLoading(true);
        await downloadFileExcelInventory(filters, `inventory.xlsx`, handleSuccess, handleError);

        setLoading(false);
    };

    const getAllPartners = () => {
        getPartners(null, resp => {
            if (resp.data) {
                const partnersData = resp.data.map((partner: CompanyDTO) => ({
                    value: partner.name,
                    text: partner.name
                }));
                setPartners(partnersData);

                const uniqueCountries = Array.from(new Set(resp.data.map((partner: CompanyDTO) => String(partner.country))));
                const countriesData = uniqueCountries.map(country => ({
                    value: country as string,
                    text: country as string
                }));
                setCountries(countriesData);
            }
        });
    };

    const getAllParts = () => {
        getParts(null, (current: any) => {
            if (current.data.length) {
                setParts(current.data);
            } else {
                setNotification(old => ({ ...old, type: 'information', message: t('hsl:translation.NotFoundParts') }));
            }
        });
    };

    const resetFilters = () => {
        setFilters(InitialFilter);
        getFilteredItems(InitialFilter);
    };

    const applyFilters = () => {
        getFilteredItems(filters);
    };

    const onAction = (inventoryItem: InventoryData) => {
        setSelectedItem(inventoryItem);
        setOpenModal(true);
    };

    const searchFieldsRender = () => {
        return (
            <div>
                <Header title={t('pages:inventory')} logo={false} menu={false}>
                    <div style={{ margin: '10px' }}>
                        <Button
                            content={t('hsl:translation.PendingItems')}
                            type="primary"
                            size="medium"
                            iconPosition="right"
                            icon={<Icon name="delivery-missed" root="common" />}
                            onClick={() => navigate('pending-items')}
                            description={t('hsl:translation.PendingItems')}
                        />
                        <RoleComponent roles={['SUPERADMIN', 'ADMIN']}>
                            <Button
                                content={t('hsl:translation.NewInventory')}
                                type="primary"
                                size="medium"
                                iconPosition="right"
                                icon={<Icon name="add" root="building" />}
                                onClick={() => navigate('delivery')}
                                description={t('hsl:translation.NewInventory')}
                            />
                        </RoleComponent>
                        <RoleComponent roles={['CLIENTADMIN', 'CLIENT']}>
                            <Button
                                content={t('hsl:translation.Deliveries')}
                                type="secondary"
                                size="medium"
                                iconPosition="right"
                                icon={
                                    <BadgedIcon
                                        root="aero"
                                        name="runway"
                                        size="large"
                                        badge={totalItemsDelivery.toString()}
                                        color={totalItemsDelivery > 0 ? 'red' : 'blue'}
                                        onClick={() => navigate('delivery')}
                                    />
                                }
                                onClick={() => navigate('delivery')}
                                description={t('hsl:translation.Deliveries')}
                            />
                        </RoleComponent>
                    </div>
                </Header>
                <div className="form4Columns">
                    <Select
                        placeholder={t('common:translation.Product')}
                        label={t('common:translation.Product')}
                        options={productOptions}
                        search={true}
                        multiple={true}
                        onChange={value => setFilters({ ...filters, ProductName: value })}
                    />
                    <Select
                        placeholder={t('common:translation.PartNumber')}
                        label={t('common:translation.PartNumber')}
                        value={filters.PartNumber}
                        options={partsOptions}
                        search={true}
                        multiple={true}
                        onChange={value => setFilters({ ...filters, PartNumber: value })}
                    />
                    <Input
                        placeholder={t('common:translation.Description')}
                        label={t('common:translation.Description')}
                        value={filters.PartNumberDescription}
                        onChange={value => setFilters({ ...filters, PartNumberDescription: value })}
                    />
                    <RoleComponent roles={['SUPERADMIN', 'ADMIN']}>
                        <Select
                            label={t('common:translation.Partner')}
                            placeholder={t('common:translation.Partner')}
                            options={partners}
                            multiple={true}
                            search={true}
                            onChange={value => setFilters({ ...filters, CompanyName: value })}
                        />
                    </RoleComponent>
                    <RoleComponent roles={['SUPERADMIN', 'ADMIN']}>
                        <Select
                            placeholder={t('user:address.Country')}
                            label={t('user:address.Country')}
                            options={countries}
                            multiple={true}
                            search={true}
                            onChange={value => setFilters({ ...filters, Country: value })}
                        />
                    </RoleComponent>
                </div>

                <div className="buttonsSearch">
                    <Button
                        type="secondary"
                        icon="badge-delete"
                        content={t('common:actions.ResetFilters')}
                        iconPosition="right"
                        onClick={resetFilters}
                    />
                    <Button
                        type="primary"
                        icon="filter1"
                        content={t('common:actions.ApplyFilters')}
                        iconPosition="right"
                        onClick={applyFilters}
                    />
                </div>
            </div>
        );
    };

    const renderModal = () => {
        return (
            <Modal open={openModal} closeIcon={true} onClose={() => setOpenModal(false)}>
                <Modal.Header>{t('common:actions.Action')}</Modal.Header>
                <Modal.Content>
                    <div className="form3Columns">
                        <Select
                            placeholder={t('common:actions.Action')}
                            label={t('common:actions.Action')}
                            options={[
                                { text: t('hsl:translation.Entry'), value: 'Entry' },
                                { text: t('hsl:translation.Exit'), value: 'Exit' }
                            ]}
                            value={newAction.Action}
                            onChange={value => setNewAction({ ...newAction, Action: value })}
                        />
                        <Input
                            placeholder={t('common:translation.Quantity')}
                            label={t('common:translation.Quantity')}
                            type="number"
                            value={newAction?.Quantity?.toString()}
                            onChange={value => setNewAction({ ...newAction, Quantity: parseInt(value, 10) })}
                        />
                        <Input
                            placeholder={t('hsl:translation.TrackingNumber')}
                            label={t('hsl:translation.TrackingNumber')}
                            value={newAction.TrackingNumber}
                            onChange={value => setNewAction({ ...newAction, TrackingNumber: value })}
                        />
                        <Input
                            placeholder={t('hsl:translation.Observations')}
                            label={t('hsl:translation.Observations')}
                            value={newAction.Observation?.toString()}
                            onChange={value => setNewAction({ ...newAction, Observation: value })}
                        />
                    </div>
                </Modal.Content>
                <Modal.Footer>
                    <Button
                        content={t('common:actions.Save')}
                        type="primary"
                        onClick={() => handleSave()}
                        loading={loadingAction}
                    />
                </Modal.Footer>
            </Modal>
        );
    };

    const renderCell = (field: string | number, value: number) => {
        const color = value <= 0 ? '#be271c' : '#303030';
        return <span style={{ color }}>{field}</span>;
    };

    const tableInventoryRender = () => {
        return (
            <div className="Table-inventory">
                <DataTable columnResizeMode="fit" data={filteredData} loading={loadingItems}>
                    <DataTable.HeaderBar>
                        <DataTable.HeaderBar.Item
                            content={t('hsl:translation.ExportExcel')}
                            iconSize="small"
                            icon="file-download"
                            onClick={handleExportExcel}
                        />
                    </DataTable.HeaderBar>
                    <DataTable.Column
                        field="ItemId"
                        align="center"
                        header={t('ID')}
                        sortable={true}
                        renderer={e => renderCell(e.rowIndex + 1, e.rowData.Quantity)}
                    />
                    <DataTable.Column
                        field="CompanyName"
                        align="center"
                        header={t('common:translation.Partner')}
                        sortable={true}
                        renderer={e => renderCell(e.rowData.CompanyName, e.rowData.Quantity)}
                    />
                    <DataTable.Column
                        field="ProductName"
                        align="center"
                        header={t('common:translation.Product')}
                        sortable={true}
                        renderer={e => renderCell(e.rowData.ProductName, e.rowData.Quantity)}
                    />
                    <DataTable.Column
                        field="PartNumber"
                        align="center"
                        header={t('Part Number')}
                        sortable={true}
                        renderer={e => renderCell(e.rowData.PartNumber, e.rowData.Quantity)}
                    />
                    <DataTable.Column
                        field="PartNumberDescription"
                        align="center"
                        header={t('common:translation.Description')}
                        sortable={true}
                        renderer={e => renderCell(e.rowData.PartNumberDescription, e.rowData.Quantity)}
                    />
                    <DataTable.Column
                        field="Quantity"
                        align="center"
                        header={t('hsl:translation.Quantity')}
                        sortable={true}
                        renderer={e => renderCell(e.rowData.Quantity, e.rowData.Quantity)}
                    />
                    <DataTable.Column
                        field="details"
                        renderer={e => (
                            <div onClick={() => navigate(`inventory-details/${e?.rowData.Id}`)}>
                                <Icon size="small" root="common" name="search" className="icon-event" />
                            </div>
                        )}
                        align="center"
                        header={t('common:translation.Details')}
                    />
                    {(userDetails?.role === '1' || userDetails?.role === '2') && (
                        <DataTable.Column
                            field="action"
                            renderer={e => (
                                <Icon
                                    size="small"
                                    root="common"
                                    name="document-edit"
                                    className="icon-event"
                                    onClick={() => onAction(e.rowData)}
                                />
                            )}
                            align="center"
                            header={t('common:actions.Action')}
                        />
                    )}
                </DataTable>
                <DataTable.Pagination
                    totalItems={totalItems}
                    itemsPerPage={10}
                    onPageChange={pageNumber => {
                        const filterPage: FilterInventoryDTO = { ...filters, PageIndex: parseInt(pageNumber.toString()) };
                        getFilteredItems(filterPage);
                    }}
                />
            </div>
        );
    };

    return (
        <>
            {searchFieldsRender()}
            {tableInventoryRender()}
            {renderModal()}
        </>
    );
}

export default InventorySearch;
